import { useCallback, useEffect, useState } from "react";
import { File, GetFileWithSiblingsSuccessResponse } from "../types/api/get-file-with-siblings-success-response";
import { useDispatch } from "react-redux";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../redux/reducer/alert-message.reducer";
import { showLoadingAlertMessage } from "../../../utils/show-loading-alert-message/show-loading-alert-message";
import { showDefaultErrorMessages } from "../../../utils/show-default-error-message/show-default-error-messages";
import { networkCall } from "../../../utils/network-call/network-call";
import { AlertMessages } from "../../../enums/alert-messages.enum";
import { AxiosResponse } from "axios";
import { GetFileWithParentSuccessResponse } from "../types/api/get-file-with-parent-success-response";
import { DefaultErrorResponse } from "../../../types/api/error/default-error-response/default-error-respose.type";
import { GetFileWithChildrenSuccessResponse } from "../types/api/get-file-with-children-success-response";
import useDebouncedFunction from "../../../hooks/use-debounced-function";
import { UpdateFileSuccessResponse } from "../types/api/update-file-success-response";

export const useFiles = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [parentFolderId, setParentFolderId] = useState<number | null>(null);
    const dispatch = useDispatch();

    const initFiles = useCallback(async () => {
        const getFilesResponse = await networkCall('/files');

        const messageShowed = showDefaultErrorMessages(getFilesResponse);
        if(messageShowed) return;

        if (getFilesResponse?.status === 200) {
            const res: AxiosResponse<GetFileWithSiblingsSuccessResponse> = getFilesResponse;
            setFiles(res.data.files);
        }
    }, []);

    useEffect(() => {
        initFiles();
    }, [initFiles]);

    const goBackByOneFolder = useCallback(async () => {
        if (parentFolderId === null) {
            dispatch(showAlertMessage({
                alertMessageType: 'error',
                alertMessageText: 'Nem léphez vissza, nincs több mappa.'
            }));
            return;
        }

        showLoadingAlertMessage();

        const response = await networkCall(`/files/${ parentFolderId }`, {
            params: {
                with: 'siblings'
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            const res = response as AxiosResponse<GetFileWithSiblingsSuccessResponse>;
            setFiles(res.data.files);

            const response2 = await networkCall(`/files/${ parentFolderId }`, {
                params: {
                    with: 'parent'
                }
            });

            if (response2?.status === 200) {
                const res2 = response2 as AxiosResponse<GetFileWithParentSuccessResponse>;
                
                if (res2.data.file.parent) setParentFolderId(res2.data.file.parent.id);
                else setParentFolderId(null);

            } else {
                dispatch(showAlertMessage({
                    alertMessageType: 'error',
                    alertMessageText: 'Hiba történt a szülőmappa lekérdezése során.'
                }));
            }

            return;
        } else if (response?.status === 404) {
            const res= response as AxiosResponse<DefaultErrorResponse>;

            switch (res.data.code) {
                case 12000:
                    alertMessageState.alertMessageText = 'A fájl/mappa nem található.';
                    break;
            }
        }

        dispatch(showAlertMessage(alertMessageState));
    }, [dispatch, parentFolderId]);

    const openFolder = useCallback(async (id: number) => {
        if (!files.some(file => (file.id === id && !file.isFile))) {
            dispatch(showAlertMessage({
                alertMessageType: 'error',
                alertMessageText: 'A mappa nem nyitható meg.'
            }));
            return false;
        }

        showLoadingAlertMessage();

        const response = await networkCall(`/files/${ id }`);

        const messageShowed = showDefaultErrorMessages(response);
        if(messageShowed) return false;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            const res = response as AxiosResponse<GetFileWithChildrenSuccessResponse>;
            setFiles(res.data.file.children ?? []);
            setParentFolderId(res.data.file.id);
            return true;
        } else if (response?.status === 404) {
            const res= response as AxiosResponse<DefaultErrorResponse>;

            switch (res.data.code) {
                case 12000:
                    alertMessageState.alertMessageText = 'A fájl/mappa nem található.';
                    break;
            }
        } else if (response?.status === 400) {
            const res= response as AxiosResponse<DefaultErrorResponse>;

            switch (res.data.code) {
                case 12001:
                    alertMessageState.alertMessageText = 'Csak mappa nyitható meg.';
                    break;
            }
        }

        dispatch(showAlertMessage(alertMessageState));
        return false;
    }, [dispatch, files]);

    const searchFiles = useDebouncedFunction(async (searchTerm: string) => {
        if (!searchTerm) {
            initFiles();
            return;
        }

        showLoadingAlertMessage();

        const response = await networkCall('/files', {
            params: {
                searchTerm
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            const res: AxiosResponse<GetFileWithSiblingsSuccessResponse> = response;
            setFiles(res.data.files);
            return;
        }

        dispatch(showAlertMessage(alertMessageState));
    });

    const deleteFile = useCallback(async (id: number) => {
        const file = files.find(_file => _file.id === id);
        if (!file) {
            alert('A fájl vagy mappa nem törölhető');
            return;
        }

        if(window.confirm(`Biztosan törlöd a "${ file.name }" nevű ${ file.isFile ? 'fájlt' : 'mappát' }?`)) {
            showLoadingAlertMessage();

            const response = await networkCall(`/files/${ file.id }`, {
                method: 'DELETE'
            });

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            if(response?.status === 204) {
                setFiles(files.filter(_file => _file.id !== id));
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                return;
            }

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = `Törlés sikertelen, a ${ file.isFile ? 'fájl' : 'mappa' } nem létezik.`;
            }

            dispatch(showAlertMessage(alertMessageState));
        }
    }, [dispatch, files]);

    const addFiles = useCallback((_files: File[]) => {
        setFiles((prevFiles) => {
            const newFiles = JSON.parse(JSON.stringify(prevFiles));
            newFiles.push(..._files);
            return newFiles;
        });
    }, []);

    const renameFolder = useCallback(async (id: number, name: string) => {
        const file = files.find(file => file.id === id);

        if (!file || file.isFile || file.name === name) return;

        showLoadingAlertMessage();

        const response = await networkCall(`/files/${ id }`, {
            method: 'PUT',
            body: { name }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if(messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {

            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            setFiles(
                prevFiles => prevFiles.map(
                    prevFile => {
                        if (prevFile.id === id)
                            return {
                                ...prevFile,
                                name
                            };

                        return prevFile;
                    }
                )
            );

        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mező.'
                    break;

            }

        }

        dispatch(showAlertMessage(alertMessageState));
    }, [dispatch, files]);

    const moveFile = useCallback(async (fileId: number, targetFolderId: number | null) => {
        showLoadingAlertMessage();

        const response = await networkCall(`/files/${ fileId }`, {
            method: 'PUT',
            body: {
                parentId: targetFolderId ? targetFolderId : 0
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return false;
        
        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            const res: AxiosResponse<UpdateFileSuccessResponse> = response;
            setFiles(
                prevFiles => [...prevFiles, res.data.file]
            );
            dispatch(showAlertMessage({
                alertMessageText: 'Fájl sikeresen áthelyezve.',
                alertMessageType: 'success'
            }));

            return true;
        } else if (response?.status === 404) {
            const res= response as AxiosResponse<DefaultErrorResponse>;

            switch (res.data.code) {
                case 12000:
                    alertMessageState.alertMessageText = 'A fájl/mappa nem található.';
                    break;
            }
        } else if (response?.status === 400) {
            const res= response as AxiosResponse<DefaultErrorResponse>;

            switch (res.data.code) {
                case 12002:
                    alertMessageState.alertMessageText = 'Csak fájl helyezhető át.';
                    break;
            }
        }

        dispatch(showAlertMessage(alertMessageState));
        return false;
    }, [dispatch]);

    return {
        files,
        parentFolderId,
        goBackByOneFolder,
        openFolder,
        addFiles,
        deleteFile,
        searchFiles: searchFiles as (searchTerm: string) => void,
        renameFolder,
        moveFile
    };
};